import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  SelectChangeEvent,
  Button,
  CircularProgress,
  Alert,
  Skeleton,
  Stack,
} from '@mui/material';
import { useAccessToken } from '@features/user';
import { useTemplateBuilder } from './TemplateBuilderContext';
import APIService from '../../services/APIService';

// Transcript interface matching the backend response structure
interface AvailableTranscript {
  transcript_id: string;
  title: string;
  date: string;
}

interface TranscriptUtterance {
  speaker: string;
  text: string;
}

interface DetailedTranscript {
  transcript_id: string;
  title: string;
  date: string;
  utterances: TranscriptUtterance[];
  metadata: Record<string, any>;
}

const TranscriptPicker: React.FC = () => {
  const { getAccessToken } = useAccessToken();
  const { transcriptId, tasksState, handleTranscriptConfirmed, processingError } =
    useTemplateBuilder();

  const [selectedTranscriptId, setSelectedTranscriptId] = useState<string>(transcriptId || '');
  const [availableTranscripts, setAvailableTranscripts] = useState<AvailableTranscript[]>([]);
  const [selectedTranscript, setSelectedTranscript] = useState<DetailedTranscript | null>(null);
  const [loadingTranscripts, setLoadingTranscripts] = useState<boolean>(false);
  const [loadingTranscriptDetails, setLoadingTranscriptDetails] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [speakers, setSpeakers] = useState<string[]>([]);

  // Determine if controls should be disabled
  const isProcessing = tasksState.transcriptAnalysis === 'processing';
  const isSelectionDisabled = isProcessing || loadingTranscripts || loadingTranscriptDetails;
  const isFileProcessed = tasksState.fileProcessing === 'completed';

  // Update selected transcript when transcriptId from context changes
  useEffect(() => {
    if (transcriptId && transcriptId !== selectedTranscriptId) {
      setSelectedTranscriptId(transcriptId);
      fetchTranscriptDetails(transcriptId);
    }
  }, [transcriptId]);

  // Extract unique speakers when transcript changes
  useEffect(() => {
    if (selectedTranscript && selectedTranscript.utterances) {
      const uniqueSpeakers = Array.from(
        new Set(selectedTranscript.utterances.map(line => line.speaker)),
      );
      setSpeakers(uniqueSpeakers);
    }
  }, [selectedTranscript]);

  // Fetch available transcripts
  useEffect(() => {
    const fetchAvailableTranscripts = async () => {
      setLoadingTranscripts(true);
      setError(null);

      try {
        const accessToken = await getAccessToken();
        const response = await APIService.makeAPIGetRequest({
          requestString: '/templatebuilder/getAvailableTranscripts',
          accessToken,
        });

        if (response.ok) {
          setAvailableTranscripts(response.value.transcripts || []);

          // Select first transcript if none selected and transcripts are available
          if (response.value.transcripts.length > 0 && !selectedTranscriptId) {
            const firstTranscriptId = response.value.transcripts[0].transcript_id;
            setSelectedTranscriptId(firstTranscriptId);
            fetchTranscriptDetails(firstTranscriptId);
          }
        } else {
          setError('Failed to load available transcripts');
        }
      } catch (error) {
        console.error('Error fetching available transcripts:', error);
        setError('Error fetching available transcripts');
      } finally {
        setLoadingTranscripts(false);
      }
    };

    fetchAvailableTranscripts();
  }, [getAccessToken]);

  // Fetch transcript details when a transcript is selected
  const fetchTranscriptDetails = async (transcriptId: string) => {
    if (!transcriptId) return;

    setLoadingTranscriptDetails(true);
    setError(null);

    try {
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIGetRequest({
        requestString: '/templatebuilder/getTranscript',
        params: { transcript_id: transcriptId },
        accessToken,
      });

      if (response.ok) {
        setSelectedTranscript(response.value.transcript);
      } else {
        setError('Failed to load transcript details');
        setSelectedTranscript(null);
      }
    } catch (error) {
      console.error('Error fetching transcript details:', error);
      setError('Error fetching transcript details');
      setSelectedTranscript(null);
    } finally {
      setLoadingTranscriptDetails(false);
    }
  };

  const handleTranscriptChange = (event: SelectChangeEvent) => {
    const newTranscriptId = event.target.value;
    setSelectedTranscriptId(newTranscriptId);
    fetchTranscriptDetails(newTranscriptId);
  };

  const handleConfirm = () => {
    if (selectedTranscriptId) {
      handleTranscriptConfirmed(selectedTranscriptId);
    }
  };

  // Skeleton transcript items for loading state
  const renderSkeletonTranscript = () => {
    return (
      <Stack spacing={1}>
        {[1, 2, 3, 4, 5].map(item => (
          <Paper key={item} elevation={1} sx={{ p: 1.5 }}>
            <Skeleton variant="text" width={80} height={24} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="90%" />
          </Paper>
        ))}
      </Stack>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: theme => `1px solid ${theme.palette.divider}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Typography variant="h6" gutterBottom>
        Pick transcript to work with
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        See your template in action with a real transcript. Pick one below and then you can review
        and customize your template.
      </Typography>

      {processingError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {processingError}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {isFileProcessed && (
        <Alert severity="success" sx={{ mb: 3 }}>
          File processed successfully. Please select a transcript to continue.
        </Alert>
      )}

      {loadingTranscripts ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 3,
              alignItems: { xs: 'stretch', sm: 'flex-end' },
            }}
          >
            <FormControl
              sx={{
                flex: 3,
                minWidth: { xs: '100%', sm: 'auto' },
              }}
              disabled={isSelectionDisabled}
            >
              <InputLabel>Select Transcript</InputLabel>
              <Select
                value={selectedTranscriptId}
                onChange={handleTranscriptChange}
                label="Select Transcript"
                sx={{
                  borderRadius: '0.5rem',
                  backgroundColor: 'background.paper',
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '0.5rem',
                      mt: 0.5,
                    },
                  },
                }}
              >
                {availableTranscripts.map(transcript => (
                  <MenuItem key={transcript.transcript_id} value={transcript.transcript_id}>
                    {transcript.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              sx={{
                flex: 1,
                height: { sm: '56px' }, // Match the height of the Select component
                minWidth: { xs: '100%', sm: '220px' },
                borderRadius: '0.5rem',
              }}
              onClick={handleConfirm}
              disabled={!selectedTranscriptId || isProcessing || loadingTranscriptDetails}
            >
              {tasksState.transcriptAnalysis === 'processing' ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                  Analyzing...
                </Box>
              ) : (
                'Generate Template'
              )}
            </Button>
          </Box>

          <Box
            sx={{
              flex: '1 1 auto',
              height: 'calc(100% - 200px)',
              maxHeight: 'calc(100vh - 300px)',
              overflowY: 'auto',
              overflowX: 'hidden',
              pb: 2,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.05)',
              },
            }}
          >
            {loadingTranscriptDetails ? (
              renderSkeletonTranscript()
            ) : selectedTranscript ? (
              <Stack spacing={1}>
                {selectedTranscript.utterances.map((utterance, index) => (
                  <Paper
                    key={index}
                    elevation={1}
                    sx={{
                      p: 1,
                      opacity: isSelectionDisabled ? 0.7 : 1,
                      transition: 'opacity 0.2s',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color={speakers.indexOf(utterance.speaker) === 0 ? 'primary' : 'secondary'}
                      sx={{ mb: 0.5 }}
                    >
                      {utterance.speaker}
                    </Typography>
                    <Typography variant="body1">{utterance.text}</Typography>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary">
                {selectedTranscriptId
                  ? 'No transcript details available'
                  : 'No transcript selected'}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default TranscriptPicker;
